import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { jsPDF } from 'jspdf';
import { MatSnackBar } from '@angular/material/snack-bar';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { ControlbordoService } from '../../services/controlbordo.service';
import { OrgStructureService } from '../../services/org-structure.service';
import { UserSessionService } from '../../services/user-session.service';
import { UserService } from '../../services/user.service';
import { API_BASE_URL } from '../../services/api-base';

interface BordoEntry {
  id: number;
  type: 'EVENT' | 'INCIDENT' | 'MEETING' | 'DECISION';
  title: string;
  description: string;
  tag?: string;
  time: string;
  dateTimeLabel?: string;
  occurred_at?: string;
  created_at?: string;
  updated_at?: string;
  diary_date?: string;
  area?: string;
  created_by?: number;
  visibility?: 'PUBLIC' | 'PRIVATE';
  severity?: 'SEV1' | 'SEV2' | 'SEV3';
  impact?: 'HIGH' | 'MEDIUM' | 'LOW';
  owner?: string;
  attachmentsCount?: number;
  isDraft?: boolean;
  draftKey?: string;
  draftSavedAt?: string;
  draftScope?: 'new' | 'edit';
  draftEntryId?: number | null;
}

interface BordoEntryForm {
  title: string;
  description: string;
  departmentId: number | null;
  clientProject: string;
  tagsList: string[];
  links: string;
  impact: BordoEntry['impact'] | null;
  severity: BordoEntry['severity'] | null;
  occurredAt: string | null;
  viewers: number[];
  incident: {
    serviceAffected: string;
    environment: string;
    status: string;
    origin: string;
    rootCauseCategories: string[];
    rootCause: string;
    actionsTaken: string;
    workaround: string;
    lessonsLearned: string;
    preventionPlan: string;
  };
  meeting: {
    agenda: string;
    participants: number[];
    participantsExternalList: string[];
    decisions: string;
    actions: string;
    meetingAt: string | null;
    meetingStartAt: string | null;
    meetingEndAt: string | null;
  };
  decision: {
    text: string;
    responsibles: number[];
    dueAt: string | null;
    impact: string;
  };
}

interface BordoEntryDraft {
  version: 1;
  tenantId: number;
  userId: number;
  type: BordoEntry['type'];
  scope: 'new' | 'edit';
  entryId: number | null;
  savedAt: string;
  entryIsPublic: boolean;
  modalDateLabel: string;
  impactStart: string | null;
  impactEnd: string | null;
  form: BordoEntryForm;
}

interface KpiCard {
  label: string;
  value: string;
  help: string;
}

interface EvidenceItem {
  name: string;
  meta: string;
  icon: string;
}

interface TemplateItem {
  title: string;
  desc: string;
}

interface AuditItem {
  action: string;
  user: string;
  time: string;
  entity: string;
}

interface IncidentDailyPoint {
  key: string;
  tickLabel: string;
  fullLabel: string;
  count: number;
}

interface IncidentMonthlyPoint {
  key: string;
  label: string;
  count: number;
  percent: number;
}

interface DepartmentDistributionPoint {
  label: string;
  count: number;
  percent: number;
}

@Component({
  selector: 'app-controlbordo',
  templateUrl: './controlbordo.component.html',
  styleUrls: ['./controlbordo.component.scss']
})
export class ControlbordoComponent implements OnInit, OnDestroy {
  @ViewChild('participantsInput') participantsInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('viewersInput') viewersInputRef?: ElementRef<HTMLInputElement>;
  tenantName = '';
  activeTab = 0;
  isPublic = false;
  entryIsPublic = false;
  showNewEntryModal = false;
  selectedEntryType: BordoEntry['type'] = 'EVENT';
  workflowStatus: 'DRAFT' | 'REVIEWED' | 'PUBLISHED' = 'DRAFT';
  dashboardDate = '';
  headerDateLabel = '';
  modalDateLabel = '';
  loadingDashboard = false;
  departmentOptions: Array<{ id: number; name: string; label: string; sectorName: string | null }> = [];
  tenantUsers: Array<{ id: number; name: string; email?: string; username?: string; label: string }> = [];
  private tenantUsersIndex = new Map<number, { id: number; name: string; email?: string; username?: string; label: string }>();
  tenantUsersLoading = false;
  participantsInputValue = '';
  viewersInputValue = '';
  rootCauseCategories = [
    'Aplicacao',
    'Banco de dados',
    'Integracao',
    'Infraestrutura',
    'Rede',
    'Seguranca',
    'Deploy',
    'Configuracao',
    'Monitoramento',
    'Processo',
    'Dados',
    'Terceiros',
    'Outros'
  ];
  incidentOrigins = ['Cliente', 'Fornecedor', 'Interno', 'Terceiro', 'Operacao', 'Produto'];
  impactStart: string | null = null;
  impactEnd: string | null = null;
  chipSeparatorKeys: number[] = [ENTER, COMMA];
  tagsInputValue = '';
  participantsExternalInputValue = '';
  filterQuickKey = '';
  filterStartDate = '';
  filterEndDate = '';
  filterSearchTerm = '';
  pageSize = 5;
  currentPage = 1;
  analyticsPeriodOptions = [7, 30, 90];
  incidentAnalyticsDays = 30;
  meetingAnalyticsDays = 30;

  quickFilters = [
    { key: 'INCIDENTS', label: 'Incidentes' },
    { key: 'DECISIONS', label: 'Decisoes' },
    { key: 'SEVS', label: 'SEVs' }
  ];

  kpis: KpiCard[] = [];
  timelineEntries: BordoEntry[] = [];
  pendings: Array<{ title: string; owner: string; due: string }> = [];
  nextSteps: string[] = [];
  dailyHighlights: string[] = [];
  attentionPoints: string[] = [];
  riskItems: string[] = [];
  handoverOpen: string[] = [];
  handoverNext: string[] = [];
  handoverOwner = '';
  evidenceList: EvidenceItem[] = [];
  templates: TemplateItem[] = [];
  auditLog: AuditItem[] = [];
  entryAttachments: EvidenceItem[] = [];
  entryFiles: File[] = [];
  draggingEntryFiles = false;
  entryExistingAttachments: Array<{
    file_name: string;
    file_path: string;
    file_size: number | null;
    uploaded_at: string | null;
  }> = [];
  isEditingEntry = false;
  editingEntryId: number | null = null;
  entryReadOnly = false;
  savingEntry = false;
  private deletingEntryIds = new Set<number>();
  private newEntryRequestUuid = '';
  private currentTenantId: number | null = null;

  entryForm: BordoEntryForm = this.buildEntryForm();
  private entryDraftAutosaveTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private snackBar: MatSnackBar,
    private bordoService: ControlbordoService,
    private orgStructureService: OrgStructureService,
    private userSession: UserSessionService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  @HostListener('window:beforeunload')
  handleBeforeUnload() {
    this.persistEntryDraft();
  }

  ngOnInit() {
    const initialUser = this.userSession.getUser();
    if (initialUser?.tenant_id) {
      this.bootstrapForUser(initialUser);
    }
    this.userSession.user$.subscribe((user) => {
      if (!user?.tenant_id) {
        return;
      }
      this.bootstrapForUser(user);
    });

    this.route.queryParams.subscribe((params) => {
      if (params && params['newEntry'] === '1') {
        this.openNewEntry('EVENT');
        this.router.navigate([], {
          queryParams: { newEntry: null },
          queryParamsHandling: 'merge'
        });
      }
    });
  }

  ngOnDestroy() {
    this.persistEntryDraft();
    this.stopEntryDraftAutosave();
  }

  private bootstrapForUser(user: any) {
    const tenantId = user?.tenant_id ?? null;
    if (!tenantId || tenantId === this.currentTenantId) {
      return;
    }
    this.currentTenantId = tenantId;
    this.tenantName = user?.tenant_name ?? user?.tenant?.tenant_name ?? user?.tenant?.name ?? '';
    this.loadDepartments(tenantId);
    this.loadTenantUsers(tenantId);
    this.loadDashboard(tenantId, user?.id ?? null);
  }

  get impactDuration(): string {
    if (!this.impactStart || !this.impactEnd) {
      return '';
    }
    const start = new Date(this.impactStart);
    const end = new Date(this.impactEnd);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      return '';
    }
    const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    if (hours <= 0) {
      return `${minutes} min`;
    }
    return `${hours}h ${remainder}m`;
  }

  private loadDepartments(tenantId: number) {
    this.orgStructureService.getDepartments(tenantId).subscribe({
      next: (departments) => {
        this.departmentOptions = this.mapDepartmentOptions(departments || []);
      },
      error: () => {
        this.departmentOptions = [];
      }
    });
  }

  private mapDepartmentOptions(departments: any[]) {
    const mapped = (departments || []).map((dept: any) => {
      const id = Number(dept.id ?? dept.department_id ?? dept.departmentId ?? 0);
      const name = dept.name ?? dept.department_name ?? dept.department ?? 'Departamento';
      const sectorName = dept.sector_name ?? dept.sectorName ?? dept.sector ?? null;
      const label = sectorName ? `${sectorName} / ${name}` : name;
      return { id, name, label, sectorName };
    });
    return [{ id: 0, name: 'Todos', label: 'Todos', sectorName: null as string | null }, ...mapped];
  }

  private resolveTenantId(explicitTenantId?: number | null) {
    if (explicitTenantId) {
      return Number(explicitTenantId);
    }
    if (this.currentTenantId) {
      return Number(this.currentTenantId);
    }
    const user = this.userSession.getUser();
    return user?.tenant_id ? Number(user.tenant_id) : null;
  }

  private loadTenantUsers(tenantId?: number | null) {
    const resolvedTenantId = this.resolveTenantId(tenantId);
    if (!resolvedTenantId) {
      this.tenantUsers = [];
      return;
    }

    this.tenantUsersLoading = true;
    this.userService.getUsersByTenant(resolvedTenantId).subscribe({
      next: (users) => {
        const mapped = this.mapTenantUsers(users || []);
        if (mapped.length) {
          this.tenantUsers = mapped;
          this.indexTenantUsers();
          this.tenantUsersLoading = false;
          return;
        }
        this.userService.getUsers().subscribe({
          next: (allUsers) => {
            const filtered = (allUsers || []).filter((user: any) => {
              const userTenantId = Number(user.tenant_id ?? user.tenantId ?? 0);
              return userTenantId === resolvedTenantId;
            });
            this.tenantUsers = this.mapTenantUsers(filtered);
            this.indexTenantUsers();
            this.tenantUsersLoading = false;
          },
          error: () => {
            this.tenantUsers = [];
            this.indexTenantUsers();
            this.tenantUsersLoading = false;
          }
        });
      },
      error: () => {
        this.userService.getUsers().subscribe({
          next: (allUsers) => {
            const filtered = (allUsers || []).filter((user: any) => {
              const userTenantId = Number(user.tenant_id ?? user.tenantId ?? 0);
              return userTenantId === resolvedTenantId;
            });
            this.tenantUsers = this.mapTenantUsers(filtered);
            this.indexTenantUsers();
            this.tenantUsersLoading = false;
          },
          error: () => {
            this.tenantUsers = [];
            this.indexTenantUsers();
            this.tenantUsersLoading = false;
          }
        });
      }
    });
  }

  private indexTenantUsers() {
    this.tenantUsersIndex = new Map(this.tenantUsers.map((user) => [user.id, user]));
  }

  private mapTenantUsers(users: any[]) {
    return (users || []).map((user: any) => {
      const name = user.name ?? user.full_name ?? user.username ?? null;
      const email = user.email ?? null;
      const username = user.username ?? null;
      const fallback = email || 'Usuario';
      const label = name ? (email ? `${name} (${email})` : name) : fallback;
      return {
        id: Number(user.id ?? user.user_id ?? 0),
        name: name || fallback,
        email: email || undefined,
        username: username || undefined,
        label
      };
    });
  }

  get selectedParticipantUsers() {
    const selected = this.entryForm?.meeting?.participants || [];
    return selected
      .map((id) => this.tenantUsersIndex.get(id))
      .filter(Boolean) as Array<{ id: number; name: string; email?: string; username?: string; label: string }>;
  }

  get filteredTenantUsers() {
    const selected = new Set(this.entryForm?.meeting?.participants || []);
    const term = (this.participantsInputValue || '').trim().toLowerCase();
    return (this.tenantUsers || []).filter((user) => {
      if (selected.has(user.id)) {
        return false;
      }
      if (!term) {
        return true;
      }
      const hay = `${user.label || ''} ${user.name || ''} ${user.email || ''} ${user.username || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }

  get selectedViewerUsers() {
    const selected = this.entryForm?.viewers || [];
    return selected
      .map((id) => this.tenantUsersIndex.get(id))
      .filter(Boolean) as Array<{ id: number; name: string; email?: string; username?: string; label: string }>;
  }

  get filteredViewerUsers() {
    const selected = new Set(this.entryForm?.viewers || []);
    const term = (this.viewersInputValue || '').trim().toLowerCase();
    return (this.tenantUsers || []).filter((user) => {
      if (selected.has(user.id)) {
        return false;
      }
      if (!term) {
        return true;
      }
      const hay = `${user.label || ''} ${user.name || ''} ${user.email || ''} ${user.username || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }

  selectParticipant(userId: number) {
    if (!this.entryForm?.meeting?.participants) {
      this.entryForm.meeting.participants = [];
    }
    if (!this.entryForm.meeting.participants.includes(userId)) {
      this.entryForm.meeting.participants.push(userId);
    }
    this.participantsInputValue = '';
    setTimeout(() => {
      this.participantsInputValue = '';
      if (this.participantsInputRef?.nativeElement) {
        this.participantsInputRef.nativeElement.value = '';
        this.participantsInputRef.nativeElement.focus();
      }
    });
    this.queueEntryDraftAutosave();
  }

  selectViewer(userId: number) {
    if (!this.entryForm?.viewers) {
      this.entryForm.viewers = [];
    }
    if (!this.entryForm.viewers.includes(userId)) {
      this.entryForm.viewers.push(userId);
    }
    this.viewersInputValue = '';
    setTimeout(() => {
      this.viewersInputValue = '';
      if (this.viewersInputRef?.nativeElement) {
        this.viewersInputRef.nativeElement.value = '';
        this.viewersInputRef.nativeElement.focus();
      }
    });
    this.queueEntryDraftAutosave();
  }

  removeParticipant(userId: number) {
    if (!this.entryForm?.meeting?.participants) {
      return;
    }
    this.entryForm.meeting.participants = this.entryForm.meeting.participants.filter((id) => id !== userId);
    this.queueEntryDraftAutosave();
  }

  removeViewer(userId: number) {
    if (!this.entryForm?.viewers) {
      return;
    }
    this.entryForm.viewers = this.entryForm.viewers.filter((id) => id !== userId);
    this.queueEntryDraftAutosave();
  }

  applyMeetingFormat(
    textarea: HTMLTextAreaElement,
    field: 'agenda' | 'decisions' | 'actions',
    format: 'bold' | 'italic' | 'bullet'
  ) {
    if (!textarea || this.entryReadOnly) {
      return;
    }
    const value = textarea.value || '';
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    let wrapperStart = '';
    let wrapperEnd = '';

    switch (format) {
      case 'bold':
        wrapperStart = '**';
        wrapperEnd = '**';
        break;
      case 'italic':
        wrapperStart = '_';
        wrapperEnd = '_';
        break;
      case 'bullet':
        wrapperStart = '\n- ';
        break;
    }

    const newValue = `${value.slice(0, start)}${wrapperStart}${value.slice(start, end)}${wrapperEnd}${value.slice(end)}`;
    this.entryForm.meeting[field] = newValue;
    setTimeout(() => {
      textarea.focus();
      const cursor = end + wrapperStart.length + wrapperEnd.length;
      textarea.selectionStart = textarea.selectionEnd = cursor;
    });
    this.queueEntryDraftAutosave();
  }

  participantChipLabel(user: { name?: string; email?: string; label?: string }) {
    const base = (user?.name || user?.label || user?.email || '').trim();
    if (!base) {
      return 'Usuario';
    }
    const withoutEmail = base.replace(/\s*\([^)]*\)\s*$/, '').trim();
    const parts = withoutEmail.split(/\s+/).filter(Boolean);
    if (parts.length <= 2) {
      return parts.join(' ');
    }
    return `${parts[0]} ${parts[parts.length - 1]}`;
  }

  openNewEntry(type: BordoEntry['type']) {
    this.persistEntryDraft();
    this.stopEntryDraftAutosave();
    this.newEntryRequestUuid = this.generateEntryRequestUuid();
    this.selectedEntryType = type;
    this.entryIsPublic = this.isPublic;
    this.entryForm = this.buildEntryForm();
    this.impactStart = null;
    this.impactEnd = null;
    this.entryFiles = [];
    this.draggingEntryFiles = false;
    this.entryExistingAttachments = [];
    this.isEditingEntry = false;
    this.editingEntryId = null;
    this.entryReadOnly = false;
    this.tagsInputValue = '';
    this.participantsExternalInputValue = '';
    this.participantsInputValue = '';
    this.viewersInputValue = '';
    this.modalDateLabel = this.formatDateTimeLabel(new Date());
    this.showNewEntryModal = true;
    if (!this.tenantUsers.length) {
      this.loadTenantUsers(this.currentTenantId ?? this.userSession.getUser()?.tenant_id ?? null);
    }
  }

  changeEntryType(type: BordoEntry['type']) {
    if (this.entryReadOnly || this.selectedEntryType === type) {
      return;
    }
    this.persistEntryDraft();
    this.selectedEntryType = type;
    this.queueEntryDraftAutosave();
  }

  closeNewEntry(preserveDraft = true) {
    if (preserveDraft) {
      this.persistEntryDraft();
    } else {
      this.clearEntryDraft();
    }
    this.stopEntryDraftAutosave();
    this.showNewEntryModal = false;
    this.isEditingEntry = false;
    this.editingEntryId = null;
    this.savingEntry = false;
    if (!preserveDraft) {
      this.newEntryRequestUuid = '';
    }
  }

  typeLabel(type: BordoEntry['type']) {
    const map: Record<string, string> = {
      EVENT: 'Evento',
      INCIDENT: 'Incidente',
      MEETING: 'Reuniao',
      DECISION: 'Decisao'
    };
    return map[type] || type;
  }

  typeIcon(type: BordoEntry['type']) {
    const map: Record<string, string> = {
      EVENT: 'add_circle_outline',
      INCIDENT: 'report_problem',
      MEETING: 'groups',
      DECISION: 'emoji_events'
    };
    return map[type] || 'notes';
  }

  workflowLabel(status: 'DRAFT' | 'REVIEWED' | 'PUBLISHED') {
    const map: Record<string, string> = {
      DRAFT: 'Rascunho',
      REVIEWED: 'Revisado',
      PUBLISHED: 'Publicado'
    };
    return map[status] || status;
  }

  getEntryCount(type: BordoEntry['type']) {
    return this.filteredTimelineEntries.filter((entry) => entry.type === type).length;
  }

  setIncidentAnalyticsDays(days: number) {
    if (!this.analyticsPeriodOptions.includes(days)) {
      return;
    }
    this.incidentAnalyticsDays = days;
  }

  setMeetingAnalyticsDays(days: number) {
    if (!this.analyticsPeriodOptions.includes(days)) {
      return;
    }
    this.meetingAnalyticsDays = days;
  }

  private analyticsChartDimensions() {
    return {
      width: 640,
      height: 230,
      left: 36,
      right: 16,
      top: 14,
      bottom: 34
    };
  }

  private incidentReferenceDate(): Date {
    if (this.dashboardDate) {
      const date = new Date(`${this.dashboardDate}T23:59:59`);
      if (!Number.isNaN(date.getTime())) {
        return date;
      }
    }
    return new Date();
  }

  private entryDate(entry: BordoEntry): Date | null {
    const raw = entry.occurred_at || entry.created_at || entry.updated_at || entry.diary_date || null;
    return this.parseBackendDate(raw);
  }

  private windowRange(days: number) {
    const end = this.incidentReferenceDate();
    const start = new Date(end);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (Math.max(1, days) - 1));
    const endLimit = new Date(end);
    endLimit.setHours(23, 59, 59, 999);
    return { start, end, endLimit };
  }

  private dailySeriesByType(type: BordoEntry['type'], days: number): IncidentDailyPoint[] {
    const { start, endLimit } = this.windowRange(days);
    const countsByDay = new Map<string, number>();
    (this.timelineEntries || [])
      .filter((entry) => entry.type === type)
      .forEach((entry) => {
        const date = this.entryDate(entry);
        if (!date || date < start || date > endLimit) {
          return;
        }
        const key = this.toIsoDate(date);
        countsByDay.set(key, (countsByDay.get(key) || 0) + 1);
      });

    const points: IncidentDailyPoint[] = [];
    for (let offset = 0; offset < days; offset += 1) {
      const day = new Date(start);
      day.setDate(start.getDate() + offset);
      const key = this.toIsoDate(day);
      points.push({
        key,
        tickLabel: day.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        fullLabel: day.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        count: countsByDay.get(key) || 0
      });
    }
    return points;
  }

  private monthlySeriesFromDaily(daily: IncidentDailyPoint[]): IncidentMonthlyPoint[] {
    const map = new Map<string, { label: string; count: number }>();
    daily.forEach((point) => {
      const monthKey = point.key.slice(0, 7);
      const labelDate = new Date(`${monthKey}-01T00:00:00`);
      const label = Number.isNaN(labelDate.getTime())
        ? monthKey
        : labelDate
            .toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
            .replace('.', '')
            .toUpperCase();
      const current = map.get(monthKey) || { label, count: 0 };
      current.count += point.count;
      map.set(monthKey, current);
    });
    const values = [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, value]) => ({ key, ...value }));
    const max = Math.max(1, ...values.map((item) => item.count));
    return values.map((item) => ({
      key: item.key,
      label: item.label,
      count: item.count,
      percent: (item.count / max) * 100
    }));
  }

  private departmentSeriesByType(type: BordoEntry['type'], days: number): DepartmentDistributionPoint[] {
    const { start, endLimit } = this.windowRange(days);
    const map = new Map<string, number>();
    (this.timelineEntries || [])
      .filter((entry) => entry.type === type)
      .forEach((entry) => {
        const date = this.entryDate(entry);
        if (!date || date < start || date > endLimit) {
          return;
        }
        const key = String(entry.area || '').trim() || 'Nao informado';
        map.set(key, (map.get(key) || 0) + 1);
      });
    const total = [...map.values()].reduce((acc, value) => acc + value, 0);
    return [...map.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({
        label,
        count,
        percent: total > 0 ? (count / total) * 100 : 0
      }));
  }

  private totalFromSeries(series: IncidentDailyPoint[]): number {
    return series.reduce((acc, point) => acc + point.count, 0);
  }

  private chartMaxFromSeries(series: IncidentDailyPoint[], average: number): number {
    const maxDaily = Math.max(0, ...series.map((point) => point.count));
    const maxAverage = Math.ceil(average);
    return Math.max(1, maxDaily, maxAverage);
  }

  private chartPointsFromSeries(series: IncidentDailyPoint[]): Array<{
    x: number;
    y: number;
    barX: number;
    barY: number;
    barWidth: number;
    barHeight: number;
    count: number;
    tickLabel: string;
    fullLabel: string;
    showLabel: boolean;
  }> {
    const dims = this.analyticsChartDimensions();
    if (!series.length) {
      return [];
    }
    const n = series.length;
    const plotWidth = dims.width - dims.left - dims.right;
    const plotHeight = this.analyticsChartPlotHeight;
    const average = this.totalFromSeries(series) / n;
    const max = this.chartMaxFromSeries(series, average);
    const barWidth = Math.max(4, plotWidth / n - 2);
    return series.map((point, index) => {
      const ratioX = n > 1 ? index / (n - 1) : 0.5;
      const x = dims.left + ratioX * plotWidth;
      const y = dims.top + ((max - point.count) / max) * plotHeight;
      const barHeight = Math.max(1, (point.count / max) * plotHeight);
      const barY = dims.top + plotHeight - barHeight;
      return {
        x,
        y,
        barX: x - barWidth / 2,
        barY,
        barWidth,
        barHeight,
        count: point.count,
        tickLabel: point.tickLabel,
        fullLabel: point.fullLabel,
        showLabel: index % Math.max(1, Math.floor(n / 6)) === 0 || index === n - 1
      };
    });
  }

  private trendPathFromPoints(points: Array<{ x: number; y: number }>): string {
    if (!points.length) {
      return '';
    }
    return points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
      .join(' ');
  }

  get incidentWindowLabel(): string {
    const { start, end } = this.windowRange(this.incidentAnalyticsDays);
    return `${this.formatDateLabel(start)} - ${this.formatDateLabel(end)}`;
  }

  get incidentDailySeries(): IncidentDailyPoint[] {
    return this.dailySeriesByType('INCIDENT', this.incidentAnalyticsDays);
  }

  get incidentMonthlySeries(): IncidentMonthlyPoint[] {
    return this.monthlySeriesFromDaily(this.incidentDailySeries);
  }

  get incidentDepartmentSeries(): DepartmentDistributionPoint[] {
    return this.departmentSeriesByType('INCIDENT', this.incidentAnalyticsDays);
  }

  get incidentTotalInWindow(): number {
    return this.totalFromSeries(this.incidentDailySeries);
  }

  get incidentDailyAverage(): number {
    return this.incidentTotalInWindow / Math.max(1, this.incidentAnalyticsDays);
  }

  get meetingWindowLabel(): string {
    const { start, end } = this.windowRange(this.meetingAnalyticsDays);
    return `${this.formatDateLabel(start)} - ${this.formatDateLabel(end)}`;
  }

  get meetingDailySeries(): IncidentDailyPoint[] {
    return this.dailySeriesByType('MEETING', this.meetingAnalyticsDays);
  }

  get meetingMonthlySeries(): IncidentMonthlyPoint[] {
    return this.monthlySeriesFromDaily(this.meetingDailySeries);
  }

  get meetingDepartmentSeries(): DepartmentDistributionPoint[] {
    return this.departmentSeriesByType('MEETING', this.meetingAnalyticsDays);
  }

  get meetingTotalInWindow(): number {
    return this.totalFromSeries(this.meetingDailySeries);
  }

  get meetingDailyAverage(): number {
    return this.meetingTotalInWindow / Math.max(1, this.meetingAnalyticsDays);
  }

  get analyticsChartViewBox(): string {
    const dims = this.analyticsChartDimensions();
    return `0 0 ${dims.width} ${dims.height}`;
  }

  get analyticsChartPlotHeight(): number {
    const dims = this.analyticsChartDimensions();
    return dims.height - dims.top - dims.bottom;
  }

  get incidentChartPoints() {
    return this.chartPointsFromSeries(this.incidentDailySeries);
  }

  get meetingChartPoints() {
    return this.chartPointsFromSeries(this.meetingDailySeries);
  }

  get incidentChartMaxValue(): number {
    return this.chartMaxFromSeries(this.incidentDailySeries, this.incidentDailyAverage);
  }

  get meetingChartMaxValue(): number {
    return this.chartMaxFromSeries(this.meetingDailySeries, this.meetingDailyAverage);
  }

  private yAxisTicksForMax(max: number): Array<{ value: number; y: number }> {
    const dims = this.analyticsChartDimensions();
    const tickCount = 4;
    const plotHeight = this.analyticsChartPlotHeight;
    const ticks: Array<{ value: number; y: number }> = [];
    for (let idx = 0; idx <= tickCount; idx += 1) {
      const value = Math.round(max - (max * idx) / tickCount);
      const y = dims.top + (plotHeight * idx) / tickCount;
      ticks.push({ value, y });
    }
    return ticks;
  }

  get incidentYAxisTicks(): Array<{ value: number; y: number }> {
    return this.yAxisTicksForMax(this.incidentChartMaxValue);
  }

  get meetingYAxisTicks(): Array<{ value: number; y: number }> {
    return this.yAxisTicksForMax(this.meetingChartMaxValue);
  }

  private averageYFor(average: number, max: number): number {
    const dims = this.analyticsChartDimensions();
    const plotHeight = this.analyticsChartPlotHeight;
    return dims.top + ((max - average) / max) * plotHeight;
  }

  get incidentAverageY(): number {
    return this.averageYFor(this.incidentDailyAverage, this.incidentChartMaxValue);
  }

  get meetingAverageY(): number {
    return this.averageYFor(this.meetingDailyAverage, this.meetingChartMaxValue);
  }

  get incidentTrendPath(): string {
    return this.trendPathFromPoints(this.incidentChartPoints);
  }

  get meetingTrendPath(): string {
    return this.trendPathFromPoints(this.meetingChartPoints);
  }

  get filteredTimelineEntries() {
    let entries = this.timelineEntries || [];
    const search = (this.filterSearchTerm || '').trim().toLowerCase();
    if (search) {
      entries = entries.filter((entry) => {
        const hay = [
          entry.title,
          entry.description,
          entry.tag,
          entry.area,
          entry.owner,
          entry.severity,
          entry.impact
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(search);
      });
    }
    if (this.filterQuickKey === 'INCIDENTS') {
      entries = entries.filter((entry) => entry.type === 'INCIDENT');
    } else if (this.filterQuickKey === 'DECISIONS') {
      entries = entries.filter((entry) => entry.type === 'DECISION');
    } else if (this.filterQuickKey === 'SEVS') {
      entries = entries.filter((entry) => !!entry.severity);
    }

    const start = this.filterStartDate ? new Date(`${this.filterStartDate}T00:00:00`) : null;
    const end = this.filterEndDate ? new Date(`${this.filterEndDate}T23:59:59.999`) : null;

    if (start || end) {
      entries = entries.filter((entry) => {
        const raw = entry.occurred_at || entry.created_at || entry.updated_at || null;
        const dateKey = this.extractDateKey(raw);
        if (!dateKey) {
          return false;
        }
        const date = new Date(`${dateKey}T00:00:00`);
        if (Number.isNaN(date.getTime())) {
          return false;
        }
        if (start && date < start) {
          return false;
        }
        if (end && date > end) {
          return false;
        }
        return true;
      });
    }

    if (this.activeTab === 1) {
      entries = entries.filter((entry) => entry.type === 'INCIDENT');
    } else if (this.activeTab === 2) {
      entries = entries.filter((entry) => entry.type === 'MEETING');
    }

    return [...entries].sort((a, b) => this.getEntrySortKey(b) - this.getEntrySortKey(a));
  }

  get totalTimelinePages() {
    const total = this.filteredTimelineEntries.length;
    return Math.max(1, Math.ceil(total / this.pageSize));
  }

  get pagedTimelineEntries() {
    const totalPages = this.totalTimelinePages;
    if (this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredTimelineEntries.slice(startIndex, startIndex + this.pageSize);
  }

  onTabChange(index: number) {
    this.activeTab = index;
    this.resetPagination();
  }

  resetPagination() {
    this.currentPage = 1;
  }

  previousPage() {
    this.currentPage = Math.max(1, this.currentPage - 1);
  }

  nextPage() {
    this.currentPage = Math.min(this.totalTimelinePages, this.currentPage + 1);
  }

  saveEntry() {
    if (this.savingEntry) {
      return;
    }
    const user = this.userSession.getUser();
    const tenantId = user?.tenant_id ?? null;
    const userId = user?.id ?? null;
    if (!tenantId || !userId) {
      this.snackBar.open('Não foi possível identificar a tenant do usuário.', 'OK', { duration: 3000 });
      return;
    }
    if (this.entryReadOnly) {
      this.snackBar.open('Você não tem permissão para alterar este registro.', 'OK', { duration: 3000 });
      return;
    }

    const draftKey = this.getEntryDraftKey();

    const title = this.entryForm.title.trim();
    if (!title) {
      this.snackBar.open('Informe o título do registro.', 'OK', { duration: 3000 });
      return;
    }

    const description = this.entryForm.description.trim();
    const areaName = this.getDepartmentName(this.entryForm.departmentId);
    if (!areaName) {
      this.snackBar.open('Informe o departamento.', 'OK', { duration: 3000 });
      return;
    }
    const clientProject = this.entryForm.clientProject.trim();

    const payload: any = {
      tenant_id: tenantId,
      user_id: userId,
      diary_date: this.dashboardDate || undefined,
      type: this.selectedEntryType,
      title,
      description: description || null,
      client_project: clientProject || null,
      visibility: this.entryIsPublic ? 'PUBLIC' : 'PRIVATE',
      viewers: this.entryIsPublic ? this.entryForm.viewers : [],
      area: areaName || null,
      impact: this.entryForm.impact || null,
      severity: this.entryForm.severity || null,
      occurred_at: this.entryForm.occurredAt || null,
      tags: this.entryForm.tagsList,
      links: this.parseLinks(this.entryForm.links)
    };

    if (this.selectedEntryType === 'INCIDENT') {
      if (!this.entryForm.severity || !this.entryForm.incident.environment || !this.entryForm.incident.status) {
        this.snackBar.open('Informe criticidade, ambiente e status do incidente.', 'OK', { duration: 3000 });
        return;
      }
      const lessonsLearned = this.entryForm.incident.lessonsLearned || null;
      const preventionPlan = this.entryForm.incident.preventionPlan || lessonsLearned || null;
      payload.incident = {
        service_affected: this.entryForm.incident.serviceAffected || null,
        environment: this.entryForm.incident.environment || null,
        impact_start: this.impactStart || null,
        impact_end: this.impactEnd || null,
        severity: this.entryForm.severity || null,
        status: this.entryForm.incident.status || null,
        origin: this.entryForm.incident.origin || null,
        root_cause: this.entryForm.incident.rootCause || null,
        actions_taken: this.entryForm.incident.actionsTaken || null,
        workaround: this.entryForm.incident.workaround || null,
        lessons_learned: lessonsLearned,
        prevention_plan: preventionPlan
      };
    }

    if (this.selectedEntryType === 'MEETING') {
      if (!this.entryForm.meeting.agenda.trim() || !this.entryForm.meeting.participants.length) {
        this.snackBar.open('Informe pauta e participantes da reunião.', 'OK', { duration: 3000 });
        return;
      }
      payload.meeting = {
        agenda: this.entryForm.meeting.agenda || null,
        participants: this.entryForm.meeting.participants || [],
        participants_text: this.entryForm.meeting.participantsExternalList.length
          ? this.entryForm.meeting.participantsExternalList.join(', ')
          : null,
        decisions_text: this.entryForm.meeting.decisions || null,
        actions_text: this.entryForm.meeting.actions || null,
        meeting_start_at: this.entryForm.meeting.meetingStartAt || null,
        meeting_end_at: this.entryForm.meeting.meetingEndAt || null,
        meeting_at: this.entryForm.meeting.meetingStartAt || this.entryForm.meeting.meetingAt || null
      };
    }

    if (this.selectedEntryType === 'EVENT') {
      payload.client_project = null;
      payload.links = [];
    }

    if (this.selectedEntryType === 'DECISION') {
      payload.decision = {
        decision_text: this.entryForm.decision.text || null,
        impact_text: this.entryForm.decision.impact || null,
        due_at: this.entryForm.decision.dueAt || null,
        responsibles: this.entryForm.decision.responsibles || []
      };
    }

    const saveRequest = this.isEditingEntry && this.editingEntryId
      ? this.bordoService.updateEntry(this.editingEntryId, payload)
      : this.bordoService.createEntry({
          ...payload,
          request_uuid: this.newEntryRequestUuid || this.generateEntryRequestUuid()
        });
    this.savingEntry = true;

    saveRequest.subscribe({
      next: (response: any) => {
        this.savingEntry = false;
        this.clearEntryDraftByKey(draftKey);
        this.stopEntryDraftAutosave();
        const entryId = this.isEditingEntry ? this.editingEntryId : response?.entry?.id;
        if (entryId && this.entryFiles.length) {
          this.bordoService.uploadEntryAttachments(entryId, tenantId, userId, this.entryFiles).subscribe({
            next: () => {
              this.snackBar.open('Registro e anexos salvos.', 'OK', { duration: 2500 });
              this.closeNewEntry(false);
              this.loadDashboard(tenantId, userId);
            },
            error: () => {
              this.snackBar.open('Registro salvo, mas falhou o upload dos anexos.', 'OK', { duration: 3500 });
              this.closeNewEntry(false);
              this.loadDashboard(tenantId, userId);
            }
          });
          return;
        }

        this.snackBar.open(this.isEditingEntry ? 'Registro atualizado.' : 'Registro salvo no diário.', 'OK', { duration: 2500 });
        this.closeNewEntry(false);
        this.loadDashboard(tenantId, userId);
      },
      error: () => {
        this.savingEntry = false;
        this.snackBar.open('Não foi possível salvar o registro.', 'OK', { duration: 3000 });
      }
    });
  }

  openEditEntry(item: BordoEntry) {
    if (item?.isDraft) {
      this.openDraftEntry(item);
      return;
    }
    this.persistEntryDraft();
    this.stopEntryDraftAutosave();
    const user = this.userSession.getUser();
    const tenantId = user?.tenant_id ?? null;
    const userId = user?.id ?? null;
    if (!tenantId) {
      this.snackBar.open('Tenant inválida para abrir o registro.', 'OK', { duration: 3000 });
      return;
    }

    this.isEditingEntry = true;
    this.editingEntryId = item.id;
    this.selectedEntryType = item.type;
    this.entryIsPublic = String(item.visibility || '').toUpperCase() === 'PUBLIC';
    this.entryReadOnly = !!userId && Number(item.created_by || 0) !== Number(userId);
    this.entryFiles = [];
    this.entryExistingAttachments = [];
    this.modalDateLabel =
      this.extractTimeFromDateTimeLabel(item.dateTimeLabel) ||
      this.formatTimeLabel(item.occurred_at || item.created_at || null) ||
      item.time ||
      this.formatTimeLabel(new Date());
    this.viewersInputValue = '';
    this.showNewEntryModal = true;

    this.bordoService.getEntryDetails(tenantId, item.id).subscribe({
      next: (response: any) => {
        const entry = response?.entry;
        if (!entry) {
          return;
        }
        this.entryForm = this.buildEntryForm();
        this.entryForm.title = entry.title || '';
        this.entryForm.description = entry.description || '';
        this.entryForm.severity = entry.severity || null;
        this.entryForm.impact = entry.impact || null;
        this.entryForm.occurredAt = this.formatForDatetimeLocal(entry.occurred_at || entry.created_at);
        this.entryIsPublic = String(entry.visibility || '').toUpperCase() === 'PUBLIC';
        this.entryReadOnly = !!userId && Number(entry.created_by || 0) !== Number(userId);

        if (entry.area) {
          const department = this.departmentOptions.find(
            (dept) => dept.label === entry.area || dept.name === entry.area
          );
          this.entryForm.departmentId = department?.id ?? null;
        }

        const tags = Array.isArray(response?.tags) ? response.tags : [];
        this.entryForm.tagsList = this.normalizeTags(tags.map((tag: any) => tag.name || tag).join(' '));

        const links = Array.isArray(response?.links) ? response.links : [];
        const clientProject = links.find((link: any) => link.link_type === 'CLIENT_PROJECT');
        this.entryForm.clientProject = clientProject?.link_ref || '';
        this.entryForm.links = links
          .filter((link: any) => link.link_type !== 'CLIENT_PROJECT')
          .map((link: any) => link.link_ref || link.link_url || '')
          .filter((value: string) => value)
          .join(', ');

        const incident = response?.incident || null;
        if (incident) {
          this.entryForm.incident.serviceAffected = incident.service_affected || incident.service_name || '';
          this.entryForm.incident.environment = incident.environment || '';
          this.entryForm.incident.status = incident.status || '';
          this.entryForm.incident.origin = incident.incident_origin || incident.origin || '';
          this.entryForm.incident.rootCause = incident.root_cause || '';
          this.entryForm.incident.actionsTaken = incident.actions_taken || '';
          this.entryForm.incident.workaround = incident.workaround || '';
          this.entryForm.incident.lessonsLearned = incident.lessons_learned || '';
          this.entryForm.incident.preventionPlan = incident.prevention_plan || '';
          this.impactStart = this.formatForDatetimeLocal(incident.impact_start);
          this.impactEnd = this.formatForDatetimeLocal(incident.impact_end);
          this.modalDateLabel =
            this.formatTimeLabel(incident.impact_start || entry.occurred_at || entry.created_at) ||
            this.modalDateLabel;
        }

        const meeting = response?.meeting || null;
        if (meeting) {
          this.entryForm.meeting.agenda = meeting.agenda || '';
          this.entryForm.meeting.participantsExternalList = this.parseExternalParticipants(meeting.participants_text);
          this.participantsExternalInputValue = '';
          this.participantsInputValue = '';
          this.entryForm.meeting.decisions = meeting.decisions_text || '';
          this.entryForm.meeting.actions = meeting.actions_text || '';
          this.entryForm.meeting.meetingStartAt = this.formatForDatetimeLocal(meeting.meeting_start_at || meeting.meeting_at);
          this.entryForm.meeting.meetingEndAt = this.formatForDatetimeLocal(meeting.meeting_end_at);
          this.entryForm.meeting.meetingAt = this.formatForDatetimeLocal(meeting.meeting_at);
          this.modalDateLabel =
            this.formatTimeLabel(meeting.meeting_start_at || meeting.meeting_at || entry.occurred_at || entry.created_at) ||
            this.modalDateLabel;
          const participants = Array.isArray(response?.meetingParticipants) ? response.meetingParticipants : [];
          this.entryForm.meeting.participants = participants.map((p: any) => p.user_id).filter(Boolean);
        }

        const entryViewers = Array.isArray(response?.entryViewers) ? response.entryViewers : [];
        this.entryForm.viewers = entryViewers.map((viewer: any) => viewer.user_id).filter(Boolean);

        const decision = response?.decision || null;
        if (decision) {
          this.entryForm.decision.text = decision.decision_text || '';
          this.entryForm.decision.impact = decision.impact_text || '';
          this.entryForm.decision.dueAt = this.formatForDateOnly(decision.due_at);
        }

        this.entryExistingAttachments = Array.isArray(response?.attachments) ? response.attachments : [];
        this.restoreEntryDraft();
        this.queueEntryDraftAutosave();
      },
      error: () => {
        this.snackBar.open('Não foi possível carregar o registro.', 'OK', { duration: 3000 });
      }
    });
  }

  deleteDraftEntry(item: BordoEntry, event: Event) {
    event.stopPropagation();
    if (!item?.isDraft || !item?.draftKey) {
      return;
    }
    this.clearEntryDraftByKey(item.draftKey);
    this.timelineEntries = this.timelineEntries.filter((entry) => entry.draftKey !== item.draftKey);
    this.resetPagination();
    this.snackBar.open('Rascunho excluído.', 'OK', { duration: 2500 });
  }

  deleteTimelineEntry(item: BordoEntry, event: Event) {
    event.stopPropagation();
    if (!item) {
      return;
    }

    if (item.isDraft) {
      this.deleteDraftEntry(item, event);
      return;
    }

    const user = this.userSession.getUser();
    const tenantId = user?.tenant_id ?? null;
    const userId = user?.id ?? null;
    const entryId = Number(item.id || 0);
    if (!tenantId || !userId || !entryId) {
      this.snackBar.open('Não foi possível identificar o registro para exclusão.', 'OK', { duration: 3000 });
      return;
    }
    if (this.deletingEntryIds.has(entryId)) {
      return;
    }

    const confirmed = window.confirm(
      `Excluir o registro "${item.title}"? Essa ação removerá a entrada e seus vínculos do diário.`
    );
    if (!confirmed) {
      return;
    }

    this.deletingEntryIds.add(entryId);
    this.bordoService.deleteEntry(entryId, tenantId, userId).subscribe({
      next: () => {
        this.deletingEntryIds.delete(entryId);
        if (this.isEditingEntry && this.editingEntryId === entryId) {
          this.closeNewEntry(false);
        }
        this.snackBar.open('Registro excluído.', 'OK', { duration: 2500 });
        this.loadDashboard(tenantId, userId);
      },
      error: (error) => {
        this.deletingEntryIds.delete(entryId);
        const message = error?.error?.error || 'Não foi possível excluir o registro.';
        this.snackBar.open(message, 'OK', { duration: 3000 });
      }
    });
  }

  private openDraftEntry(item: BordoEntry) {
    this.persistEntryDraft();
    this.stopEntryDraftAutosave();

    const user = this.userSession.getUser();
    const tenantId = user?.tenant_id ?? null;
    const userId = user?.id ?? null;
    if (!tenantId || !userId) {
      this.snackBar.open('Não foi possível identificar o rascunho.', 'OK', { duration: 3000 });
      return;
    }

    const draft = this.readEntryDraftByKey(item.draftKey || null);
    if (!draft) {
      this.snackBar.open('O rascunho não foi encontrado no navegador.', 'OK', { duration: 3000 });
      return;
    }

    this.selectedEntryType = draft.type || item.type;
    this.isEditingEntry = draft.scope === 'edit' && !!draft.entryId;
    this.editingEntryId = this.isEditingEntry ? draft.entryId : null;
    this.entryIsPublic = !!draft.entryIsPublic;
    this.entryReadOnly = false;
    this.entryFiles = [];
    this.entryExistingAttachments = [];
    this.entryForm = {
      ...this.buildEntryForm(),
      ...draft.form,
      incident: {
        ...this.buildEntryForm().incident,
        ...(draft.form?.incident || {})
      },
      meeting: {
        ...this.buildEntryForm().meeting,
        ...(draft.form?.meeting || {})
      },
      decision: {
        ...this.buildEntryForm().decision,
        ...(draft.form?.decision || {})
      }
    };
    this.impactStart = draft.impactStart ?? null;
    this.impactEnd = draft.impactEnd ?? null;
    this.modalDateLabel = draft.modalDateLabel || item.dateTimeLabel || item.time || this.formatDateTimeLabel(new Date());
    this.showNewEntryModal = true;
    this.viewersInputValue = '';
    this.participantsInputValue = '';
    this.participantsExternalInputValue = '';
    this.tagsInputValue = '';
    this.entryForm.viewers = Array.isArray(draft.form?.viewers) ? [...draft.form.viewers] : [];

    if (this.isEditingEntry && this.editingEntryId) {
      this.bordoService.getEntryDetails(tenantId, this.editingEntryId).subscribe({
        next: (response: any) => {
          this.entryExistingAttachments = Array.isArray(response?.attachments) ? response.attachments : [];
          this.restoreEntryDraft();
          this.queueEntryDraftAutosave();
        },
        error: () => {
          this.restoreEntryDraft();
          this.queueEntryDraftAutosave();
        }
      });
      return;
    }

    this.restoreEntryDraft();
    this.queueEntryDraftAutosave();
  }

  downloadEntryPdf(item: BordoEntry, event: Event) {
    event.stopPropagation();
    if (item?.isDraft) {
      this.snackBar.open('Salve o rascunho antes de gerar o PDF.', 'OK', { duration: 3000 });
      return;
    }
    const user = this.userSession.getUser();
    const tenantId = user?.tenant_id ?? null;
    if (!tenantId) {
      this.snackBar.open('Tenant inválida para baixar PDF.', 'OK', { duration: 3000 });
      return;
    }

    this.bordoService.getEntryDetails(tenantId, item.id).subscribe({
      next: async (response: any) => {
        try {
          const entry = response?.entry;
          if (!entry) {
            this.snackBar.open('Registro não encontrado.', 'OK', { duration: 3000 });
            return;
          }

          const doc = new jsPDF({ unit: 'pt', format: 'a4' });
          const logoDataUrl = await this.getPdfBrandLogoDataUrl(response?.tenant?.logo_url);
          const factDateLabel = this.formatFactDateLabel(entry, response);
          const headerContext = {
            title: 'Diário de Bordo',
            subtitle: `${this.typeLabel(entry.type)} · ${factDateLabel}`,
            logoDataUrl
          };

          let cursorY = this.drawPdfDocumentHeader(doc, headerContext, entry);

          cursorY = this.drawPdfSectionTable(doc, 'Resumo do registro', [
            { label: 'Data do fato', value: factDateLabel },
            { label: 'Título', value: entry.title || '—' },
            { label: 'Descrição', value: entry.description || '—' },
            { label: 'Área', value: entry.area || '—' },
            { label: 'Criticidade', value: this.pdfSeverityLabel(entry.severity) },
            { label: 'Impacto', value: this.pdfImpactLabel(entry.impact) },
            { label: 'Criado por', value: entry.owner_name || '—' }
          ], cursorY + 10, headerContext, entry);

          if (response?.incident) {
            const incident = response.incident;
            const impactStart = incident.impact_start || null;
            const impactEnd = incident.impact_end || null;
            cursorY = this.drawPdfSectionTable(doc, 'Detalhes do incidente', [
              { label: 'Serviço afetado', value: incident.service_affected || incident.service_name || '—' },
              { label: 'Ambiente', value: incident.environment || '—' },
              { label: 'Status', value: incident.status || '—' },
              { label: 'Origem', value: incident.incident_origin || incident.origin || '—' },
              { label: 'Início do impacto', value: this.formatDateTimeLabel(impactStart) || '—' },
              { label: 'Fim do impacto', value: this.formatDateTimeLabel(impactEnd) || '—' },
              { label: 'Duração do impacto', value: this.pdfDurationLabel(impactStart, impactEnd, incident.impact_window_minutes) },
              { label: 'Categorias causa raiz', value: this.pdfListText(incident.root_cause_categories) },
              { label: 'Causa raiz', value: incident.root_cause || '—' },
              { label: 'Ações tomadas', value: incident.actions_taken || '—' },
              { label: 'Workaround', value: incident.workaround || '—' },
              { label: 'Lições aprendidas', value: incident.lessons_learned || '—' },
              { label: 'Plano preventivo', value: incident.prevention_plan || incident.preventive_plan || '—' }
            ], cursorY + 8, headerContext, entry);
          }

          if (response?.meeting) {
            const meeting = response.meeting;
            const meetingStart = meeting.meeting_start_at || meeting.meeting_at || null;
            const meetingEnd = meeting.meeting_end_at || null;
            cursorY = this.drawPdfSectionTable(doc, 'Reunião', [
              { label: 'Pauta', value: meeting.agenda || '—' },
              { label: 'Participantes internos', value: this.pdfMeetingParticipantsInternal(response?.meetingParticipants) },
              { label: 'Participantes externos', value: meeting.participants_text || meeting.participants || '—' },
              { label: 'Decisões', value: meeting.decisions_text || '—' },
              { label: 'Discussões', value: meeting.discussions || '—' },
              { label: 'Notas', value: meeting.notes || '—' },
              { label: 'Encaminhamentos', value: meeting.actions_text || '—' },
              { label: 'Data/hora início', value: this.formatDateTimeLabel(meetingStart) || '—' },
              { label: 'Data/hora fim', value: this.formatDateTimeLabel(meetingEnd) || '—' },
              { label: 'Duração', value: this.pdfDurationLabel(meetingStart, meetingEnd) }
            ], cursorY + 8, headerContext, entry);
          }

          if (response?.decision) {
            const decision = response.decision;
            cursorY = this.drawPdfSectionTable(doc, 'Decisão', [
              { label: 'Decisão tomada', value: decision.decision_text || '—' },
              { label: 'Impacto esperado', value: decision.impact_text || '—' },
              { label: 'Prazo', value: this.formatDateLabel(decision.due_at) || '—' },
              { label: 'Status', value: decision.status || '—' }
            ], cursorY + 8, headerContext, entry);
          }

          const tags = Array.isArray(response?.tags) ? response.tags.map((tag: any) => tag.name || tag).join(', ') : '';
          if (tags) {
            cursorY = this.drawPdfSectionTable(doc, 'Tags', [{ label: 'Tags aplicadas', value: tags }], cursorY + 8, headerContext, entry);
          }

          const links = Array.isArray(response?.links)
            ? response.links
                .map((link: any) => link.link_ref || link.link_url || '')
                .filter((value: string) => value)
                .join('\n')
            : '';
          if (links) {
            cursorY = this.drawPdfSectionTable(doc, 'Links relacionados', [{ label: 'Links', value: links }], cursorY + 8, headerContext, entry);
          }

          // Anexos removidos temporariamente do PDF.

          this.drawPdfFooter(doc);
          doc.save(`controlbordo-${entry.id}-${this.pdfTypeFileSlug(entry.type)}.pdf`);
        } catch (error) {
          // Ajuda no diagnóstico quando apenas alguns registros quebram a geração.
          console.error('Falha ao gerar PDF da entrada', item?.id, error);
          this.snackBar.open('Não foi possível gerar o PDF.', 'OK', { duration: 3000 });
        }
      },
      error: () => {
        this.snackBar.open('Não foi possível gerar o PDF.', 'OK', { duration: 3000 });
      }
    });
  }

  onEntryDragOver(event: DragEvent) {
    event.preventDefault();
    this.draggingEntryFiles = true;
  }

  onEntryDragLeave() {
    this.draggingEntryFiles = false;
  }

  onEntryDrop(event: DragEvent) {
    event.preventDefault();
    this.draggingEntryFiles = false;
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.addEntryFiles(files);
  }

  onEntryFileSelect(event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const files = Array.from(input.files ?? []);
    this.addEntryFiles(files);
    input.value = '';
  }

  removeEntryFile(index: number) {
    this.entryFiles = this.entryFiles.filter((_, i) => i !== index);
  }

  fileSizeLabel(bytes: number) {
    if (!bytes && bytes !== 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  }

  attachmentUrl(file: any) {
    return file?.file_path || '#';
  }

  private loadDashboard(tenantId: number, userId?: number | null) {
    this.loadingDashboard = true;
    const today = this.toIsoDate(new Date());
    const resolvedUserId = userId ?? this.userSession.getUser()?.id ?? null;
    if (!resolvedUserId) {
      this.loadingDashboard = false;
      this.snackBar.open('Usuário inválido para carregar o diário.', 'OK', { duration: 3000 });
      return;
    }
    this.bordoService.getDashboard(tenantId, today, resolvedUserId).subscribe({
      next: (response: any) => {
        this.loadingDashboard = false;
        const dateValue = response?.date || '';
        this.dashboardDate = dateValue;
        this.headerDateLabel = dateValue ? this.formatDateLabel(dateValue) : 'Hoje';

        const diary = response?.diary || null;
        if (diary) {
          const visibility = String(diary.visibility || '').toUpperCase();
          this.isPublic = visibility === 'PUBLIC';
          this.workflowStatus = diary.status || 'DRAFT';
          this.dailyHighlights = this.parseList(diary.highlights);
          this.attentionPoints = this.parseList(diary.attention_points);
          this.riskItems = this.parseList(diary.risks);
          this.nextSteps = this.parseList(diary.next_steps);
          this.handoverOpen = this.parseList(diary.pendings);
          this.handoverNext = this.parseList(diary.next_steps);
          this.handoverOwner = diary.area || '';
        } else {
          this.isPublic = false;
          this.workflowStatus = 'DRAFT';
          this.dailyHighlights = [];
          this.attentionPoints = [];
          this.riskItems = [];
          this.nextSteps = [];
          this.handoverOpen = [];
          this.handoverNext = [];
          this.handoverOwner = '';
        }

        const entries = Array.isArray(response?.entries) ? response.entries : [];
        const mappedEntries = entries.map((entry: any) => ({
          id: Number(entry.id),
          type: entry.type,
          title: entry.title,
          description: entry.description || '',
          tag: entry.tag || null,
          time: entry.time_label || this.formatTimeLabel(entry.occurred_at || entry.created_at),
          dateTimeLabel: this.formatDateTimeLabel(entry.updated_at || entry.occurred_at || entry.created_at),
          occurred_at: entry.occurred_at || null,
          created_at: entry.created_at || null,
          updated_at: entry.updated_at || null,
          diary_date: entry.diary_date || null,
          area: entry.area || null,
          visibility: entry.visibility ? String(entry.visibility).toUpperCase() : undefined,
          created_by: Number(entry.created_by || 0) || undefined,
          severity: entry.severity || null,
          impact: entry.impact || null,
          owner: entry.owner_name || null,
          attachmentsCount: Number(entry.attachments_count || 0)
        }));
        this.timelineEntries = this.mergeEntryDrafts(mappedEntries);
        this.resetPagination();

        const actions = Array.isArray(response?.actions) ? response.actions : [];
        this.pendings = actions.map((item: any) => ({
          title: item.title || 'Ação',
          owner: item.owner_name || '—',
          due: item.due_at ? this.formatDateLabel(item.due_at) : ''
        }));

        const kpis = response?.kpis || {};
        const avgValue = kpis?.avg_resolution?.value;
        const avgLabel = avgValue !== null && avgValue !== undefined ? String(avgValue) : '—';
        this.kpis = [
          { label: 'Incidentes na semana', value: String(kpis?.incidents_week?.value ?? 0), help: kpis?.incidents_week?.help || '' },
          { label: 'Reunioes nos ultimos 7 dias', value: avgLabel, help: kpis?.avg_resolution?.help || '' },
          { label: 'Eventos nos ultimos 7 dias', value: String(kpis?.recurring_risks?.value ?? 0), help: kpis?.recurring_risks?.help || '' },
          { label: 'Total de acoes nos ultimos 7 dias', value: String(kpis?.overdue_actions?.value ?? 0), help: kpis?.overdue_actions?.help || '' }
        ];
      },
      error: () => {
        this.loadingDashboard = false;
        this.kpis = [];
        this.timelineEntries = this.loadEntryDraftTimelineEntries();
        this.pendings = [];
        this.headerDateLabel = this.formatDateLabel(new Date());
      }
    });
  }

  private mergeEntryDrafts(entries: BordoEntry[]): BordoEntry[] {
    const draftEntries = this.loadEntryDraftTimelineEntries();
    return [...draftEntries, ...entries].sort((a, b) => this.getEntrySortKey(b) - this.getEntrySortKey(a));
  }

  private loadEntryDraftTimelineEntries(): BordoEntry[] {
    const user = this.userSession.getUser();
    const tenantId = this.currentTenantId ?? user?.tenant_id ?? null;
    const userId = user?.id ?? null;
    if (!tenantId || !userId || typeof localStorage === 'undefined') {
      return [];
    }

    const prefix = `controlbordo:draft:${tenantId}:${userId}:`;
    const drafts: BordoEntry[] = [];

    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key || !key.startsWith(prefix)) {
        continue;
      }

      const raw = localStorage.getItem(key);
      if (!raw) {
        continue;
      }

      try {
        const draft = JSON.parse(raw) as BordoEntryDraft | null;
        if (!draft || draft.version !== 1) {
          continue;
        }
        if (Number(draft.tenantId) !== Number(tenantId) || Number(draft.userId) !== Number(userId)) {
          continue;
        }
        drafts.push(this.buildDraftTimelineEntry(draft, key));
      } catch (error) {
        console.warn('Não foi possível ler um rascunho do diário de bordo.', error);
      }
    }

    return drafts;
  }

  private buildDraftTimelineEntry(draft: BordoEntryDraft, key: string): BordoEntry {
    const savedAt = draft.savedAt || new Date().toISOString();
    const title = (draft.form?.title || '').trim() || `${this.typeLabel(draft.type)} sem título`;
    const description = (draft.form?.description || '').trim() || 'Rascunho ainda não salvo no diário.';
    const pseudoId = -Math.abs(this.hashString(key || `${draft.type}:${savedAt}`));
    const currentUser: any = this.userSession.getUser();
    return {
      id: pseudoId,
      type: draft.type,
      title,
      description,
      tag: 'Rascunho',
      time: `Rascunho · ${this.formatTimeLabel(savedAt)}`,
      dateTimeLabel: `Rascunho · ${this.formatDateTimeLabel(savedAt)}`,
      occurred_at: savedAt,
      created_at: savedAt,
      updated_at: savedAt,
      diary_date: this.dashboardDate || this.toIsoDate(new Date(savedAt)),
      area: this.getDepartmentName(draft.form?.departmentId ?? null) || undefined,
      visibility: draft.entryIsPublic ? 'PUBLIC' : 'PRIVATE',
      created_by: draft.userId,
      owner: currentUser?.['name'] || currentUser?.['username'] || 'Usuario',
      attachmentsCount: 0,
      isDraft: true,
      draftKey: key,
      draftSavedAt: savedAt,
      draftScope: draft.scope,
      draftEntryId: draft.entryId
    };
  }

  private addEntryFiles(files: File[]) {
    if (!files.length) return;
    this.entryFiles = [...this.entryFiles, ...files];
  }

  private buildEntryForm() {
    return {
      title: '',
      description: '',
      departmentId: null as number | null,
      clientProject: '',
      tagsList: [] as string[],
      links: '',
      impact: null as BordoEntry['impact'] | null,
      severity: null as BordoEntry['severity'] | null,
      occurredAt: null as string | null,
      viewers: [] as number[],
      incident: {
        serviceAffected: '',
        environment: '',
        status: '',
        origin: '',
        rootCauseCategories: [] as string[],
        rootCause: '',
        actionsTaken: '',
        workaround: '',
        lessonsLearned: '',
        preventionPlan: ''
      },
      meeting: {
        agenda: '',
        participants: [] as number[],
        participantsExternalList: [] as string[],
        decisions: '',
        actions: '',
        meetingAt: null as string | null,
        meetingStartAt: null as string | null,
        meetingEndAt: null as string | null
      },
      decision: {
        text: '',
        responsibles: [] as number[],
        dueAt: null as string | null,
        impact: ''
      }
    };
  }

  queueEntryDraftAutosave() {
    if (!this.showNewEntryModal || this.entryReadOnly) {
      return;
    }
    if (!this.hasEntryDraftContent()) {
      this.clearEntryDraft();
      this.stopEntryDraftAutosave();
      return;
    }
    this.stopEntryDraftAutosave();
    this.entryDraftAutosaveTimer = window.setTimeout(() => {
      this.persistEntryDraft();
      this.entryDraftAutosaveTimer = null;
    }, 1200);
  }

  private stopEntryDraftAutosave() {
    if (this.entryDraftAutosaveTimer) {
      clearTimeout(this.entryDraftAutosaveTimer);
      this.entryDraftAutosaveTimer = null;
    }
  }

  private getEntryDraftScope(): 'new' | 'edit' {
    return this.isEditingEntry && this.editingEntryId ? 'edit' : 'new';
  }

  private getEntryDraftKey(type: BordoEntry['type'] = this.selectedEntryType, scope: 'new' | 'edit' = this.getEntryDraftScope()) {
    const user = this.userSession.getUser();
    const tenantId = this.currentTenantId ?? user?.tenant_id ?? null;
    const userId = user?.id ?? null;
    if (!tenantId || !userId) {
      return null;
    }
    const entryIdSegment = scope === 'edit' && this.editingEntryId ? `:${this.editingEntryId}` : '';
    return `controlbordo:draft:${tenantId}:${userId}:${type}:${scope}${entryIdSegment}`;
  }

  private snapshotEntryDraft(): BordoEntryDraft | null {
    const key = this.getEntryDraftKey();
    const user = this.userSession.getUser();
    const tenantId = this.currentTenantId ?? user?.tenant_id ?? null;
    const userId = user?.id ?? null;
    if (!key || !tenantId || !userId || !this.showNewEntryModal || this.entryReadOnly) {
      return null;
    }
    if (!this.hasEntryDraftContent()) {
      return null;
    }

    return {
      version: 1,
      tenantId: Number(tenantId),
      userId: Number(userId),
      type: this.selectedEntryType,
      scope: this.getEntryDraftScope(),
      entryId: this.isEditingEntry ? this.editingEntryId : null,
      savedAt: new Date().toISOString(),
      entryIsPublic: this.entryIsPublic,
      modalDateLabel: this.modalDateLabel,
      impactStart: this.impactStart,
      impactEnd: this.impactEnd,
      form: JSON.parse(JSON.stringify(this.entryForm))
    };
  }

  private persistEntryDraft() {
    const draft = this.snapshotEntryDraft();
    if (!draft) {
      return;
    }
    const key = this.getEntryDraftKey(draft.type, draft.scope);
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(draft));
    } catch (error) {
      console.warn('Não foi possível salvar o rascunho do diário de bordo.', error);
    }
  }

  private generateEntryRequestUuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `entry-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private clearEntryDraft(type: BordoEntry['type'] = this.selectedEntryType, scope: 'new' | 'edit' = this.getEntryDraftScope()) {
    const key = this.getEntryDraftKey(type, scope);
    if (!key) {
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Não foi possível limpar o rascunho do diário de bordo.', error);
    }
  }

  private clearEntryDraftByKey(key: string | null) {
    if (!key) {
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Não foi possível limpar o rascunho do diário de bordo.', error);
    }
  }

  private restoreEntryDraft() {
    const key = this.getEntryDraftKey();
    if (!key) {
      return false;
    }

    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return false;
      }
      const draft = JSON.parse(raw) as BordoEntryDraft | null;
      if (!draft || draft.version !== 1) {
        return false;
      }
      if (Number(draft.tenantId) !== Number(this.currentTenantId ?? this.userSession.getUser()?.tenant_id ?? 0)) {
        return false;
      }
      if (Number(draft.userId) !== Number(this.userSession.getUser()?.id ?? 0)) {
        return false;
      }

      this.selectedEntryType = draft.type || this.selectedEntryType;
      this.entryIsPublic = !!draft.entryIsPublic;
      this.modalDateLabel = draft.modalDateLabel || this.modalDateLabel;
      this.impactStart = draft.impactStart ?? null;
      this.impactEnd = draft.impactEnd ?? null;
      this.entryForm = {
        ...this.buildEntryForm(),
        ...draft.form,
        incident: {
          ...this.buildEntryForm().incident,
          ...(draft.form?.incident || {})
        },
        meeting: {
          ...this.buildEntryForm().meeting,
          ...(draft.form?.meeting || {})
        },
        decision: {
          ...this.buildEntryForm().decision,
          ...(draft.form?.decision || {})
        }
      };
      return true;
    } catch (error) {
      console.warn('Não foi possível restaurar o rascunho do diário de bordo.', error);
      return false;
    }
  }

  private hasEntryDraftContent(): boolean {
    const title = (this.entryForm?.title || '').trim();
    const description = (this.entryForm?.description || '').trim();
    const incident = this.entryForm?.incident || null;
    const meeting = this.entryForm?.meeting || null;
    const decision = this.entryForm?.decision || null;
    const hasTags = Array.isArray(this.entryForm?.tagsList) && this.entryForm.tagsList.length > 0;
    const links = this.parseLinks(this.entryForm?.links || '');
    const hasLinks = links.length > 0;
    const hasViewers = Array.isArray(this.entryForm?.viewers) && this.entryForm.viewers.length > 0;
    const hasIncidentContent = !!(
      incident?.serviceAffected?.trim() ||
      incident?.environment?.trim() ||
      incident?.status?.trim() ||
      incident?.origin?.trim() ||
      incident?.rootCause?.trim() ||
      incident?.actionsTaken?.trim() ||
      incident?.workaround?.trim() ||
      incident?.lessonsLearned?.trim() ||
      incident?.preventionPlan?.trim() ||
      (Array.isArray(incident?.rootCauseCategories) && incident.rootCauseCategories.length)
    );
    const hasMeetingContent = !!(
      meeting?.agenda?.trim() ||
      meeting?.decisions?.trim() ||
      meeting?.actions?.trim() ||
      meeting?.meetingAt ||
      meeting?.meetingStartAt ||
      meeting?.meetingEndAt ||
      (Array.isArray(meeting?.participants) && meeting.participants.length) ||
      (Array.isArray(meeting?.participantsExternalList) && meeting.participantsExternalList.length)
    );
    const hasDecisionContent = !!(
      decision?.text?.trim() ||
      decision?.impact?.trim() ||
      decision?.dueAt ||
      (Array.isArray(decision?.responsibles) && decision.responsibles.length)
    );

    return !!(
      title ||
      description ||
      this.entryForm?.departmentId ||
      this.entryForm?.clientProject?.trim() ||
      this.entryForm?.occurredAt ||
      hasTags ||
      hasLinks ||
      hasViewers ||
      this.entryForm?.impact ||
      this.entryForm?.severity ||
      hasIncidentContent ||
      hasMeetingContent ||
      hasDecisionContent
    );
  }

  private parseTags(value: string) {
    return (value || '')
      .split(/[,\s]+/)
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  private normalizeTags(value: string): string[] {
    const tags = this.parseTags(value);
    const normalized = tags
      .map((tag) => tag.replace(/^#+/, '').trim())
      .filter(Boolean);
    return Array.from(new Set(normalized));
  }

  private parseExternalParticipants(value: string): string[] {
    return (value || '')
      .split(/[,\n;]+/)
      .map((name) => name.trim())
      .filter(Boolean);
  }

  addTagFromInput(event: MatChipInputEvent) {
    const value = (event.value || '').trim();
    if (!value) {
      event.chipInput?.clear();
      this.tagsInputValue = '';
      return;
    }
    const nextTags = this.normalizeTags(value);
    nextTags.forEach((tag) => {
      if (!this.entryForm.tagsList.includes(tag)) {
        this.entryForm.tagsList.push(tag);
      }
    });
    event.chipInput?.clear();
    this.tagsInputValue = '';
    this.queueEntryDraftAutosave();
  }

  removeTag(tag: string) {
    this.entryForm.tagsList = this.entryForm.tagsList.filter((item) => item !== tag);
    this.queueEntryDraftAutosave();
  }

  addExternalParticipant(event: MatChipInputEvent) {
    const value = (event.value || '').trim();
    if (!value) {
      event.chipInput?.clear();
      this.participantsExternalInputValue = '';
      return;
    }
    const nextNames = this.parseExternalParticipants(value);
    nextNames.forEach((name) => {
      if (!this.entryForm.meeting.participantsExternalList.includes(name)) {
        this.entryForm.meeting.participantsExternalList.push(name);
      }
    });
    event.chipInput?.clear();
    this.participantsExternalInputValue = '';
    this.queueEntryDraftAutosave();
  }

  removeExternalParticipant(name: string) {
    this.entryForm.meeting.participantsExternalList = this.entryForm.meeting.participantsExternalList.filter(
      (item) => item !== name
    );
    this.queueEntryDraftAutosave();
  }

  private parseLinks(value: string) {
    return (value || '')
      .split(/[,\n]+/)
      .map((link) => link.trim())
      .filter(Boolean);
  }

  private formatForDatetimeLocal(value: any) {
    const date = this.parseBackendDate(value);
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private formatForDateOnly(value: any) {
    const date = this.parseBackendDate(value);
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseList(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string') return [];
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    } catch (err) {
      // ignore
    }
    return trimmed
      .split(/\r?\n|;|\u2022|\*/g)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  private toIsoDate(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseBackendDate(value: any): Date | null {
    if (!value && value !== 0) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const trimmed = String(value).trim();
    if (!trimmed) return null;

    const brDateTimeMatch = trimmed.match(
      /^(\d{2})\/(\d{2})\/(\d{4})(?:,\s*|\s+)(\d{2}):(\d{2})(?::(\d{2}))?$/
    );
    if (brDateTimeMatch) {
      const [, day, month, year, hour, minute, second] = brDateTimeMatch;
      const parsed = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second || '0')
      );
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const brDateOnlyMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (brDateOnlyMatch) {
      const [, day, month, year] = brDateOnlyMatch;
      const parsed = new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const isoMatch = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:?\d{2})?$/
    );
    if (isoMatch) {
      const [, year, month, day, hour, minute, second, milli, timezone] = isoMatch;
      const hh = Number(hour || '0');
      const mm = Number(minute || '0');
      const ss = Number(second || '0');
      const ms = Number((milli || '0').padEnd(3, '0'));

      if (timezone) {
        const normalizedTz = /^[+-]\d{4}$/.test(timezone)
          ? `${timezone.slice(0, 3)}:${timezone.slice(3)}`
          : timezone;
        const iso = `${year}-${month}-${day}T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}.${String(ms).padStart(3, '0')}${normalizedTz}`;
        const parsed = new Date(iso);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }

      const parsed = new Date(Number(year), Number(month) - 1, Number(day), hh, mm, ss, ms);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const fallback = new Date(trimmed);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  private formatDateLabel(dateValue: string | Date) {
    const date = this.parseBackendDate(dateValue);
    if (!date) {
      return '';
    }
    return date.toLocaleDateString('pt-BR');
  }

  private formatDateTimeLabel(dateValue: string | Date) {
    const date = this.parseBackendDate(dateValue);
    if (!date) {
      return '';
    }
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private formatTimeLabel(dateValue: string | Date | null) {
    if (!dateValue) return '';
    const date = this.parseBackendDate(dateValue);
    if (!date) {
      return '';
    }
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  private extractDateKey(value: any): string | null {
    if (!value) return null;
    if (typeof value === 'string') {
      const match = value.trim().match(/^\d{4}-\d{2}-\d{2}$/);
      if (match) {
        return match[0];
      }
    }
    const date = this.parseBackendDate(value);
    if (!date) {
      return null;
    }
    return this.toIsoDate(date);
  }

  private getEntrySortKey(entry: BordoEntry) {
    const raw: unknown = entry.draftSavedAt || entry.updated_at || entry.occurred_at || entry.created_at || entry.diary_date || null;
    if (!raw) return 0;
    const date = this.parseBackendDate(raw);
    return date ? date.getTime() : 0;
  }

  private readEntryDraftByKey(key: string | null): BordoEntryDraft | null {
    if (!key || typeof localStorage === 'undefined') {
      return null;
    }
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return null;
      }
      const draft = JSON.parse(raw) as BordoEntryDraft | null;
      if (!draft || draft.version !== 1) {
        return null;
      }
      return draft;
    } catch (error) {
      console.warn('Não foi possível carregar o rascunho do diário de bordo.', error);
      return null;
    }
  }

  private hashString(value: string): number {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
      hash = ((hash << 5) - hash) + value.charCodeAt(index);
      hash |= 0;
    }
    return hash;
  }

  private drawPdfDocumentHeader(
    doc: jsPDF,
    context: { title: string; subtitle: string; logoDataUrl: string | null },
    entry: any
  ) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 30;
    const headerX = margin;
    const headerY = 22;
    const headerWidth = pageWidth - margin * 2;
    const headerHeight = 104;
    const brand = { navy: [18, 55, 92] as [number, number, number], teal: [24, 178, 165] as [number, number, number] };

    doc.setDrawColor(201, 217, 234);
    doc.setFillColor(250, 252, 255);
    doc.roundedRect(headerX, headerY, headerWidth, headerHeight, 10, 10, 'FD');

    const innerPadding = 14;
    const columnsGap = 16;
    const usableWidth = headerWidth - innerPadding * 2 - columnsGap;
    const leftColumnW = usableWidth * 0.30;
    const rightColumnW = usableWidth - leftColumnW;
    const leftColumnX = headerX + innerPadding;
    const rightColumnX = leftColumnX + leftColumnW + columnsGap;

    const logoBoxX = leftColumnX;
    const logoBoxY = headerY + 10;
    const logoBoxW = leftColumnW;
    const logoBoxH = headerHeight - 20;

    if (context.logoDataUrl) {
      this.drawPdfBrandLogo(doc, context.logoDataUrl, logoBoxX, logoBoxY, logoBoxW, logoBoxH);
    }

    const typeChip = this.typeLabel(entry?.type);
    const chipText = typeChip;
    const chipWidth = Math.min(220, doc.getTextWidth(chipText) + 22);
    const chipHeight = 24;
    const chipX = rightColumnX + rightColumnW - chipWidth;
    const chipY = headerY + 72;

    const textX = rightColumnX;
    const textMaxW = Math.max(150, rightColumnW - chipWidth - 16);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(brand.navy[0], brand.navy[1], brand.navy[2]);
    doc.text(doc.splitTextToSize(context.title.toUpperCase(), textMaxW), textX, headerY + 46);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(81, 106, 133);
    doc.text(doc.splitTextToSize(context.subtitle, textMaxW), textX, headerY + 64);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(48, 76, 107);
    doc.text(doc.splitTextToSize(`Empresa: ${this.tenantName || 'ControlPS'}`, textMaxW), textX, headerY + 82);
    doc.text(doc.splitTextToSize(`Registro #${entry?.id || '—'}`, textMaxW), textX, headerY + 97);

    doc.setFillColor(232, 248, 246);
    doc.setDrawColor(170, 227, 222);
    doc.roundedRect(chipX, chipY, chipWidth, chipHeight, 12, 12, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(18, 114, 106);
    doc.text(chipText, chipX + chipWidth / 2, chipY + 16, { align: 'center' });

    return headerY + headerHeight + 12;
  }

  private drawPdfSectionTable(
    doc: jsPDF,
    title: string,
    rows: Array<{ label: string; value: string }>,
    startY: number,
    headerContext: { title: string; subtitle: string; logoDataUrl: string | null },
    entry: any
  ) {
    const margin = 30;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;
    const labelWidth = 178;
    const labelPadding = 12;
    const valueWidth = contentWidth - labelWidth - labelPadding * 2;
    const sectionHeaderHeight = 24;
    const drawSectionHeader = (headerY: number, continued = false) => {
      doc.setFillColor(21, 74, 123);
      doc.roundedRect(margin, headerY, contentWidth, sectionHeaderHeight, 6, 6, 'F');
      doc.setFillColor(24, 178, 165);
      doc.rect(margin, headerY, 8, sectionHeaderHeight, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(continued ? `${title} (continuação)` : title, margin + 16, headerY + 16);
    };
    let cursorY = startY;

    cursorY = this.ensurePdfSectionSpace(doc, cursorY, sectionHeaderHeight + 26, headerContext, entry);
    drawSectionHeader(cursorY);
    cursorY += sectionHeaderHeight;

    rows.forEach((row, index) => {
      const text = this.pdfSafeText(row.value || '—');
      const allLines = this.pdfSplitTextToSize(doc, text, valueWidth);
      let linesQueue = allLines.length ? [...allLines] : ['—'];
      let chunkIndex = 0;

      while (linesQueue.length) {
        const minRowHeight = 28;
        let availableHeight = this.pdfContentBottom(doc) - cursorY;

        if (availableHeight < minRowHeight) {
          const nextY = this.ensurePdfSectionSpace(doc, cursorY, sectionHeaderHeight + minRowHeight + 4, headerContext, entry);
          if (nextY !== cursorY) {
            cursorY = nextY;
            drawSectionHeader(cursorY, true);
            cursorY += sectionHeaderHeight;
          }
          availableHeight = this.pdfContentBottom(doc) - cursorY;
        }

        let maxLinesInChunk = Math.floor((availableHeight - 12) / 12);
        if (maxLinesInChunk < 1) {
          maxLinesInChunk = 1;
        }
        if (maxLinesInChunk > linesQueue.length) {
          maxLinesInChunk = linesQueue.length;
        }

        let chunkLines = linesQueue.slice(0, maxLinesInChunk);
        let rowHeight = Math.max(minRowHeight, chunkLines.length * 12 + 12);
        while (chunkLines.length > 1 && rowHeight > availableHeight) {
          chunkLines = chunkLines.slice(0, -1);
          rowHeight = Math.max(minRowHeight, chunkLines.length * 12 + 12);
        }
        linesQueue = linesQueue.slice(chunkLines.length);

        doc.setFillColor(index % 2 === 0 ? 250 : 245, 250, 255);
        doc.rect(margin, cursorY, contentWidth, rowHeight, 'F');
        doc.setFillColor(237, 245, 252);
        doc.rect(margin, cursorY, labelWidth, rowHeight, 'F');

        doc.setDrawColor(211, 226, 241);
        doc.rect(margin, cursorY, contentWidth, rowHeight);
        doc.line(margin + labelWidth, cursorY, margin + labelWidth, cursorY + rowHeight);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(63, 86, 114);
        const label = chunkIndex === 0 ? `${row.label}:` : `${row.label} (continuação):`;
        doc.text(label, margin + 10, cursorY + 17);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(29, 49, 74);
        doc.text(chunkLines as string[], margin + labelWidth + labelPadding, cursorY + 17);

        cursorY += rowHeight;
        chunkIndex += 1;

        if (linesQueue.length) {
          const nextY = this.ensurePdfSectionSpace(doc, cursorY, sectionHeaderHeight + minRowHeight + 4, headerContext, entry);
          if (nextY !== cursorY) {
            cursorY = nextY;
            drawSectionHeader(cursorY, true);
            cursorY += sectionHeaderHeight;
          }
        }
      }
    });

    return cursorY + 12;
  }

  private ensurePdfSectionSpace(
    doc: jsPDF,
    cursorY: number,
    blockHeight: number,
    headerContext: { title: string; subtitle: string; logoDataUrl: string | null },
    entry: any
  ) {
    if (cursorY + blockHeight <= this.pdfContentBottom(doc)) {
      return cursorY;
    }
    doc.addPage();
    return this.drawPdfDocumentHeader(doc, headerContext, entry);
  }

  private pdfContentBottom(doc: jsPDF) {
    return doc.internal.pageSize.getHeight() - 64;
  }

  private drawPdfFooter(doc: jsPDF) {
    const totalPages = doc.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 30;

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setDrawColor(24, 178, 165);
      doc.line(margin, pageHeight - 34, pageWidth - margin, pageHeight - 34);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(85, 109, 136);
      doc.text('ControlPS · Diario de Bordo · Relatorio executivo', margin, pageHeight - 18);
      doc.text('Classificacao: Uso interno', pageWidth / 2, pageHeight - 18, { align: 'center' });
      doc.text(
        `Gerado em ${this.formatDateTimeLabel(new Date())} · Página ${i}/${totalPages}`,
        pageWidth - margin,
        pageHeight - 18,
        { align: 'right' }
      );
    }
  }

  private pdfSeverityLabel(value?: string | null): string {
    const map: Record<string, string> = {
      SEV1: 'SEV1 · Alta',
      SEV2: 'SEV2 · Média',
      SEV3: 'SEV3 · Baixa'
    };
    return map[String(value || '').toUpperCase()] || (String(value || '').trim() || '—');
  }

  private pdfImpactLabel(value?: string | null): string {
    const map: Record<string, string> = {
      HIGH: 'Alto',
      MEDIUM: 'Médio',
      LOW: 'Baixo'
    };
    return map[String(value || '').toUpperCase()] || (String(value || '').trim() || '—');
  }

  private pdfVisibilityLabel(value?: string | null): string {
    const visibility = String(value || '').toUpperCase();
    if (visibility === 'PUBLIC') return 'Pública';
    if (visibility === 'PRIVATE') return 'Privada';
    return '—';
  }

  private pdfDurationLabel(startValue?: string | null, endValue?: string | null, minutesValue?: any): string {
    const numericMinutes = Number(minutesValue);
    if (Number.isFinite(numericMinutes) && numericMinutes > 0) {
      return this.minutesToDurationText(numericMinutes);
    }
    const start = startValue ? new Date(startValue) : null;
    const end = endValue ? new Date(endValue) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      return '—';
    }
    const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
    return this.minutesToDurationText(minutes);
  }

  private minutesToDurationText(totalMinutes: number): string {
    if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
      return '—';
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours <= 0) {
      return `${minutes} min`;
    }
    if (minutes <= 0) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}min`;
  }

  private pdfListText(value: any): string {
    if (!value) return '—';
    if (Array.isArray(value)) {
      const joined = value.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
      return joined || '—';
    }
    const raw = String(value).trim();
    if (!raw) return '—';
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const joined = parsed.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
        return joined || '—';
      }
    } catch {
      // texto simples
    }
    return raw;
  }

  private pdfMeetingParticipantsInternal(participants: any[]): string {
    if (!Array.isArray(participants) || !participants.length) {
      return '—';
    }
    const names = participants
      .map((participant: any) => Number(participant?.user_id || participant?.id || participant))
      .filter((id: number) => Number.isFinite(id) && id > 0)
      .map((id: number) => {
        const user = this.tenantUsersIndex.get(id);
        return this.participantChipLabel(user || { label: `Usuário #${id}` });
      });
    const unique = Array.from(new Set(names.filter(Boolean)));
    return unique.length ? unique.join(', ') : '—';
  }

  private formatFactDateLabel(entry: any, response: any): string {
    const type = String(entry?.type || '').toUpperCase();
    const incident = response?.incident || null;
    const meeting = response?.meeting || null;

    let factDate: any = entry?.occurred_at || null;
    if (type === 'INCIDENT') {
      factDate = incident?.impact_start || entry?.occurred_at || incident?.created_at || entry?.created_at || null;
    } else if (type === 'MEETING') {
      factDate = meeting?.meeting_start_at || meeting?.meeting_at || entry?.occurred_at || meeting?.created_at || entry?.created_at || null;
    } else if (type === 'DECISION') {
      factDate = response?.decision?.decided_at || entry?.occurred_at || response?.decision?.created_at || entry?.created_at || null;
    } else {
      factDate = entry?.occurred_at || entry?.created_at || null;
    }

    return this.formatDateTimeLabel(factDate) || 'não informada';
  }

  private async buildPdfAttachmentPreviewItems(attachments: any[]): Promise<Array<{
    name: string;
    size: string;
    type: string;
    uploadedAt: string;
    ext: string;
    isImage: boolean;
    imageDataUrl: string | null;
  }>> {
    const items = Array.isArray(attachments) ? attachments : [];
    return Promise.all(
      items.map(async (attachment: any) => {
        const name = String(attachment?.file_name || 'arquivo');
        const type = String(attachment?.file_type || '').trim();
        const size = this.fileSizeLabel(Number(attachment?.file_size || 0));
        const uploadedAt = this.formatDateTimeLabel(attachment?.uploaded_at) || '—';
        const ext = this.fileExtensionFromName(name);
        const isImage = this.isImageAttachmentForPdf(name, type);
        const path = String(attachment?.file_path || '').trim();
        const imageDataUrl = isImage ? await this.fetchImageDataUrlForPdf(path) : null;
        return { name, size, type: type || '—', uploadedAt, ext, isImage, imageDataUrl };
      })
    );
  }

  private drawPdfAttachmentPreviewCards(
    doc: jsPDF,
    attachments: Array<{
      name: string;
      size: string;
      type: string;
      uploadedAt: string;
      ext: string;
      isImage: boolean;
      imageDataUrl: string | null;
    }>,
    startY: number,
    headerContext: { title: string; subtitle: string; logoDataUrl: string | null },
    entry: any
  ) {
    if (!attachments.length) {
      return startY;
    }
    const margin = 30;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - margin * 2;
    let lastCursorY = startY;

    attachments.forEach((attachment, index) => {
      doc.addPage();
      let cursorY = this.drawPdfDocumentHeader(doc, headerContext, entry);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(18, 55, 92);
      doc.text(`Preview do anexo ${index + 1}/${attachments.length}`, margin, cursorY + 12);
      cursorY += 20;

      const previewH = Math.max(260, pageHeight - cursorY - 140);
      doc.setFillColor(248, 252, 255);
      doc.setDrawColor(211, 226, 241);
      doc.roundedRect(margin, cursorY, contentWidth, previewH, 8, 8, 'FD');

      const previewPad = 12;
      const boxX = margin + previewPad;
      const boxY = cursorY + previewPad;
      const boxW = contentWidth - previewPad * 2;
      const boxH = previewH - previewPad * 2;

      if (attachment.imageDataUrl) {
        try {
          const props: any = doc.getImageProperties(attachment.imageDataUrl);
          const originalW = Number(props?.width || 0);
          const originalH = Number(props?.height || 0);
          const ratio = originalW && originalH ? Math.min(boxW / originalW, boxH / originalH) : 0;
          if (ratio > 0) {
            const drawW = originalW * ratio;
            const drawH = originalH * ratio;
            const drawX = boxX + (boxW - drawW) / 2;
            const drawY = boxY + (boxH - drawH) / 2;
            const format = this.pdfImageFormatFromDataUrl(attachment.imageDataUrl);
            doc.addImage(attachment.imageDataUrl, format, drawX, drawY, drawW, drawH, undefined, 'FAST');
          }
        } catch {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(36);
          doc.setTextColor(79, 107, 137);
          doc.text(attachment.ext, margin + contentWidth / 2, cursorY + previewH / 2, { align: 'center' });
        }
      } else {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(36);
        doc.setTextColor(79, 107, 137);
        doc.text(attachment.ext, margin + contentWidth / 2, cursorY + previewH / 2, { align: 'center' });
      }

      const metaY = cursorY + previewH + 18;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(24, 55, 90);
      doc.text(this.pdfSafeText(attachment.name), margin, metaY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80, 103, 128);
      doc.text(`Tipo: ${this.pdfSafeText(attachment.type)}  •  Tamanho: ${this.pdfSafeText(attachment.size)}  •  Enviado em: ${this.pdfSafeText(attachment.uploadedAt)}`, margin, metaY + 14);
      lastCursorY = metaY + 20;
    });

    return lastCursorY;
  }

  private fileExtensionFromName(fileName: string): string {
    const name = String(fileName || '').toLowerCase();
    const idx = name.lastIndexOf('.');
    if (idx < 0) return 'ARQ';
    return name.slice(idx + 1).toUpperCase();
  }

  private pdfSafeText(value: any): string {
    if (value === null || value === undefined) return '—';
    const text = String(value);
    // Remove caracteres de controle/combinação que podem quebrar renderização no jsPDF
    // preservando quebras de linha para manter a formatação do texto.
    return text
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\t/g, '    ')
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, ' ')
      .replace(/[ \u00A0]+/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim() || '—';
  }

  private pdfSplitTextToSize(doc: jsPDF, text: string, maxWidth: number): string[] {
    const normalized = this.pdfSafeText(text);
    const paragraphs = normalized.split('\n');
    const lines: string[] = [];
    paragraphs.forEach((paragraph, index) => {
      const chunk = paragraph.trim()
        ? doc.splitTextToSize(paragraph, maxWidth)
        : [' '];
      lines.push(...chunk);
      if (index < paragraphs.length - 1) {
        lines.push(' ');
      }
    });
    return lines.length ? lines : ['—'];
  }

  private extractTimeFromDateTimeLabel(value: any): string | null {
    if (!value) return null;
    const text = String(value).trim();
    if (!text) return null;
    const match = text.match(/(\d{2}:\d{2})/);
    return match ? match[1] : null;
  }

  private isImageAttachmentForPdf(fileName: string, fileType: string): boolean {
    const type = String(fileType || '').toLowerCase();
    if (type.startsWith('image/')) return true;
    const name = String(fileName || '').toLowerCase();
    return ['.png', '.jpg', '.jpeg', '.webp', '.bmp', '.gif'].some((ext) => name.endsWith(ext));
  }

  private resolveAttachmentAbsoluteUrl(filePath: string): string {
    const path = String(filePath || '').trim();
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return `${API_BASE_URL}${normalized}`;
  }

  private buildAttachmentCandidateUrls(filePath: string): string[] {
    const path = String(filePath || '').trim();
    if (!path) return [];
    if (/^https?:\/\//i.test(path)) return [path];
    const normalized = path.startsWith('/') ? path : `/${path}`;
    const urls = [`${API_BASE_URL}${normalized}`];
    const originUrl = `${window.location.origin}${normalized}`;
    if (!urls.includes(originUrl)) {
      urls.push(originUrl);
    }
    return urls;
  }

  private async fetchImageDataUrlForPdf(filePath: string): Promise<string | null> {
    const candidates = this.buildAttachmentCandidateUrls(filePath);
    for (const candidate of candidates) {
      try {
        const response = await fetch(encodeURI(candidate), { mode: 'cors' });
        if (!response.ok) {
          continue;
        }
        const blob = await response.blob();
        const dataUrl = await new Promise<string | null>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
        if (dataUrl) {
          return dataUrl;
        }
      } catch {
        // tenta próxima URL candidata
      }
    }
    return null;
  }

  private async getPdfBrandLogoDataUrl(tenantLogoUrl?: string | null): Promise<string | null> {
    const tenantLogo = String(tenantLogoUrl || '').trim();
    if (tenantLogo) {
      const tenantLogoDataUrl = await this.fetchImageDataUrlForPdf(tenantLogo);
      if (tenantLogoDataUrl) {
        return tenantLogoDataUrl;
      }
    }
    try {
      const response = await fetch(encodeURI('assets/MIV/brand/versao 1.png'));
      if (!response.ok) {
        return null;
      }
      const blob = await response.blob();
      return await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }

  private pdfTypeFileSlug(value?: string | null): string {
    const type = String(value || '').toUpperCase();
    if (type === 'INCIDENT') return 'incidente';
    if (type === 'MEETING') return 'reuniao';
    if (type === 'DECISION') return 'decisao';
    return 'evento';
  }

  private drawPdfBrandLogo(
    doc: jsPDF,
    logoDataUrl: string,
    boxX: number,
    boxY: number,
    boxW: number,
    boxH: number
  ) {
    try {
      const imageProps: any = doc.getImageProperties(logoDataUrl);
      const originalW = Number(imageProps?.width || 0);
      const originalH = Number(imageProps?.height || 0);
      if (!originalW || !originalH) return;

      const padding = 0;
      const maxW = Math.max(20, boxW - padding * 2);
      const maxH = Math.max(20, boxH - padding * 2);
      const ratio = Math.min(maxW / originalW, maxH / originalH);
      const drawW = Math.max(1, originalW * ratio);
      const drawH = Math.max(1, originalH * ratio);
      const drawX = boxX + (boxW - drawW) / 2;
      const drawY = boxY + (boxH - drawH) / 2;

      const format = this.pdfImageFormatFromDataUrl(logoDataUrl);
      doc.addImage(logoDataUrl, format, drawX, drawY, drawW, drawH, undefined, 'FAST');
    } catch {
      // fallback sem logo
    }
  }

  private pdfImageFormatFromDataUrl(dataUrl: string): 'PNG' | 'JPEG' | 'WEBP' {
    const value = String(dataUrl || '').toLowerCase();
    if (value.startsWith('data:image/jpeg') || value.startsWith('data:image/jpg')) {
      return 'JPEG';
    }
    if (value.startsWith('data:image/webp')) {
      return 'WEBP';
    }
    return 'PNG';
  }

  private getDepartmentName(departmentId: number | string | null) {
    if (departmentId === null || departmentId === undefined || departmentId === '') {
      return null;
    }
    const normalizedId = Number(departmentId);
    if (Number.isNaN(normalizedId)) {
      return null;
    }
    const department = this.departmentOptions.find((item) => Number(item.id) === normalizedId);
    return department?.label || department?.name || null;
  }
}
